// ---------------------------------------------------------------------------
// Three states, and the middle one is the default.
//
// "system" is not a cop-out setting: it is the only one that stays right when
// the creator's machine switches at sunset. A product that picks for them is
// wrong twice a day.
//
// The value is per-browser, not per-account. A theme is a property of where
// somebody is sitting — the same creator wants dark in the edit bay and light
// on a laptop by a window — so syncing it to the server would make one of those
// two places wrong every time they moved.
// ---------------------------------------------------------------------------

export type Theme = 'system' | 'light' | 'dark';

const KEY = 'rookery.theme';

export function readTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    // Private windows and blocked site data throw on access rather than
    // returning null. The theme is a convenience; failing to read it must
    // never stop the studio loading.
    return 'system';
  }
}

/**
 * Stamp the choice on the root, or remove the stamp so the OS decides.
 *
 * Removing is what makes "system" work: the stylesheet's light block is
 * `:root:not([data-theme="dark"])` inside a prefers-color-scheme query, so an
 * absent attribute is the state where the media query is in charge. Writing
 * `data-theme="system"` instead would match neither block and leave the page
 * on the dark defaults regardless of the OS.
 */
export function applyTheme(t: Theme): void {
  const root = document.documentElement;
  if (t === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', t);
  try { localStorage.setItem(KEY, t); } catch { /* see readTheme */ }
}

/** What the page is ACTUALLY showing right now, which "system" does not say. */
export function resolved(t: Theme): 'light' | 'dark' {
  if (t !== 'system') return t;
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}
